import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { CaseBattlesHistory } from '../interfaces/case-battles-history.interface';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { capitalizeFirstLetter } from '@core/utils/capitalize-first-letter.util';
import { ICaseBattle } from '@features/games/case-battles/interfaces/case-battles.interface';
import { User } from '@core/interfaces/user.interface';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { ModalService } from '@core/modals/services/modal.service';
import { CostComponent } from '@core/components/cost/cost.component';

interface Props {
  handleBattleNavigate: (uid: ICaseBattle['uid']) => void;
  modalService: ModalService;
  user?: User;
}

export const caseBattlesHistoryConfig = ({
  handleBattleNavigate,
  modalService,
  user,
}: Props): ColumnDef<CaseBattlesHistory>[] => [
  {
    accessorFn: (row) => row.uid,
    id: 'uid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'UID',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: cell.row.original.uid,
          cursor: 'pointer',
        },
        outputs: {
          handleClick: () => {
            handleBattleNavigate(cell.row.original.uid);
            modalService.close();
          },
        },
      });
    },
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Amount',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as number;
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: value,
          balanceType: cell.row.original.balancetype,
          gap: 4,
          color: 'var(--yellow)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.outcome,
    id: 'outcome',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Payout',
      };
    },
    cell: (cell) => {
      const { value, outcome, mode, participants } = cell.row.original;
      const battleProfit = mode === 'equity' ? outcome / participants : outcome;

      const getOutcomeColor = () => {
        if (battleProfit >= value) {
          return 'var(--green)';
        } else if (battleProfit === 0) {
          return 'var(--red)';
        } else {
          return 'var(--yellow)';
        }
      };

      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: battleProfit,
          balanceType: cell.row.original.balancetype,
          gap: 4,
          color: getOutcomeColor(),
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.mode,
    id: 'mode',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Mode',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as string;
      const battle = cell.row.original;
      const getColorByMode = () => {
        const colors = {
          nuclear: 'var(--case-battle-nuclear)',
          crazy: 'var(--case-battle-crazy)',
          equity: 'var(--case-battle-equity)',
          normal: 'var(--case-battle-normal)',
        };

        return colors[battle.mode as keyof typeof colors];
      };
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: capitalizeFirstLetter(value),
          color: getColorByMode(),
        },
      });
    },
  },
  {
    accessorFn: (row) => row.participants,
    id: 'participants',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Players',
      };
    },
    cell: (cell) => cell.getValue(),
  },
  {
    accessorFn: (row) => row.winner.map((w) => w.name).join(', '),
    id: 'result',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Result',
      };
    },
    cell: (cell) => {
      const isWinner = cell.row.original.winner.some(
        (u) => u.steamid === user?.steamid
      );
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: isWinner ? 'Won' : 'Lost',
          color: isWinner ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.time,
    id: 'time',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Date',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as number;
      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value: value * 1000,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.id,
    id: 'fairness',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Fairness',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `provably-fair/casebattles/${cell.row.original.id}`,
        },
        outputs: {
          handleClick: () => {
            modalService.close();
          },
        },
      });
    },
    enableSorting: true,
  },
];
